import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Bell,
  CalendarDays,
  Camera,
  ChevronDown,
  ClipboardCheck,
  Globe,
  HeartPulse,
  Mail,
  MapPin,
  MessageCircle,
  PhoneCall,
  Pill,
  Play,
  Shield,
  Star,
  UserPlus,
  Wallet
} from 'lucide-react';
import AdirePattern from '../components/shared/AdirePattern';
import LogoLockup from '../components/shared/LogoLockup';

const PROCESS_STEPS = [
  {
    icon: UserPlus,
    title: 'Sign up and complete your KYC',
    description: 'Create your Magnolia account, verify your identity, and tell us how you want to receive care updates.',
    tone: 'cream'
  },
  {
    icon: HeartPulse,
    title: 'Add the dependant you want care for',
    description: 'Share their home address, health context, mobility needs, and exact location pin so care can be planned properly.',
    tone: 'ivory'
  },
  {
    icon: Wallet,
    title: 'Choose a care package',
    description: 'Select the visit frequency that fits your family. Pricing stays flexible while Magnolia finalises package rates.',
    tone: 'wash'
  },
  {
    icon: Shield,
    title: 'Magnolia assigns a care officer',
    description: 'A trained care officer is matched using location, availability, language, and care preferences.',
    tone: 'burgundy'
  },
  {
    icon: CalendarDays,
    title: 'Visits are scheduled',
    description: 'Supervisors schedule visits in WAT so families, officers, and dependant routines stay aligned.',
    tone: 'cream'
  },
  {
    icon: ClipboardCheck,
    title: 'Structured visits are completed',
    description: 'Care officers record vitals, food supplies, home conditions, medication notes, administration support, and wellbeing observations.',
    tone: 'ivory'
  },
  {
    icon: Pill,
    title: 'Medication needs are coordinated',
    description: 'When prescriptions or refills are needed, Magnolia helps procure medicines through partner pharmacy chains and supports timely administration during care visits where required.',
    tone: 'wash'
  },
  {
    icon: Camera,
    title: 'You receive verified reports',
    description: 'Reports include supervisor review, GPS metadata, photos or videos, and clear next steps.',
    tone: 'wash'
  },
  {
    icon: Bell,
    title: 'Alerts and expenses are raised',
    description: 'When groceries, medication procurement, utilities, or urgent care needs come up, Magnolia brings them to your attention for approval.',
    tone: 'burgundy'
  }
];

const MOCK_SCREENS = [
  {
    title: 'Family dashboard',
    eyebrow: 'Today',
    type: 'dashboard',
    summary: 'See your next visit, current alerts, assigned care officers, and care expenses in one calm view.'
  },
  {
    title: 'Visit report',
    eyebrow: 'Verified',
    type: 'report',
    summary: 'Vitals, wellbeing scores, observations, and supervisor review stay together in the visit record.'
  },
  {
    title: 'Evidence gallery',
    eyebrow: 'Photo + video',
    type: 'evidence',
    summary: 'Photos and videos show food supplies, physical condition, drinking water, and anything that needs attention.'
  },
  {
    title: 'Care chat',
    eyebrow: 'Messages',
    type: 'chat',
    summary: 'Open a secure conversation with the care officer linked to a visit or dependant.'
  },
  {
    title: 'Alerts and approvals',
    eyebrow: 'Action needed',
    type: 'alerts',
    summary: 'Review urgent alerts, approve care expenses, and keep medication procurement visible before anything is bought.'
  }
];

const EVIDENCE_PHOTOS = [
  {
    label: 'Rice supply',
    meta: 'Pantry check',
    alt: 'Bowl of rice photographed during a pantry check',
    src: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?auto=format&fit=crop&w=500&q=80'
  },
  {
    label: 'Groceries',
    meta: 'Fresh restock',
    alt: 'Fresh groceries arranged on a kitchen counter',
    src: 'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=500&q=80'
  },
  {
    label: 'Condition',
    meta: 'Wellbeing check',
    alt: 'Caregiver checking an older adult during a home visit',
    src: 'https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=500&q=80'
  },
  {
    label: 'Water',
    meta: 'Drinking supply',
    alt: 'Clean drinking water bottles in a kitchen',
    src: 'https://images.unsplash.com/photo-1548839140-29a749e1cf4d?auto=format&fit=crop&w=500&q=80'
  }
];

const TRUSTED_MARKETS = [
  { code: 'us', name: 'US', src: 'https://flagcdn.com/w40/us.png' },
  { code: 'ca', name: 'Canada', src: 'https://flagcdn.com/w40/ca.png' },
  { code: 'gb', name: 'UK', src: 'https://flagcdn.com/w40/gb.png' },
  { code: 'au', name: 'Australia', src: 'https://flagcdn.com/w40/au.png' }
];

const PHARMACY_NETWORK = [
  { name: 'Medplus', src: '/assets/medplus-logo.jpg' },
  { name: 'Nett Pharmacy', src: '/assets/nett-pharmacy-logo.png' },
  { name: 'Mediccy', src: '/assets/mediccy-logo.webp' },
  { name: 'Cari-Med', src: '/assets/carimed-logo.png' }
];

const STORIES = [
  {
    quote: 'I used to call three neighbours before I could sleep. Now one verified report tells me Mum has been seen, fed, and heard.',
    name: 'Adaeze Nwosu',
    location: 'Calgary, Canada',
    image: 'https://images.unsplash.com/photo-1643724825665-0cf79dba404b?auto=format&fit=crop&crop=faces&w=160&h=160&q=80'
  },
  {
    quote: 'My father needed medication and power credit on the same day. Magnolia raised the alert, sourced the refill, and showed me every next step.',
    name: 'Kweku Mensah',
    location: 'London, UK',
    image: 'https://images.unsplash.com/photo-1559528237-1080bed3b2cb?auto=format&fit=crop&crop=faces&w=160&h=160&q=80'
  },
  {
    quote: 'The photos show the small things I would check myself if I were home: food, water, cleanliness, mood, everything.',
    name: 'Folake Adeyemi',
    location: 'Houston, USA',
    image: 'https://images.unsplash.com/photo-1645484094476-c8afbc69a878?auto=format&fit=crop&crop=faces&w=160&h=160&q=80'
  },
  {
    quote: 'It feels less like outsourcing care and more like having a reliable person walk into the house on our behalf.',
    name: 'Tari Ebi',
    location: 'Toronto, Canada',
    image: 'https://images.unsplash.com/photo-1769636929354-59165ba73c7e?auto=format&fit=crop&crop=faces&w=160&h=160&q=80'
  }
];

const FOOTER_LINKS = {
  Product: [
    ['How it works', '#how-it-works'],
    ['Family dashboard', '#family-dashboard'],
    ['Care reports', '#care-reports'],
    ['Coverage', '#coverage']
  ],
  Company: [
    ['Stories', '#stories'],
    ['About Magnolia', '#about'],
    ['Partners', '#partners'],
    ['Create account', '/register']
  ]
};

const toneClasses = {
  cream: 'border-[#E8E6E3] bg-[#F7F4EE] text-[#1A1A1A]',
  ivory: 'border-[#E8E6E3] bg-[#FAFAF7] text-[#1A1A1A]',
  wash: 'border-[#E3C9CE] bg-[#F5E8EA] text-[#1A1A1A]',
  burgundy: 'border-[#9F1D35] bg-[#9F1D35] text-[#FAFAF7]'
};

const Eyebrow = ({ children, light = false }) => (
  <p className={`caption mb-5 flex items-center gap-3 ${light ? 'text-[#F5E8EA]' : 'text-[#9F1D35]'}`}>
    <span className={`h-px w-6 ${light ? 'bg-[#C9A961]' : 'bg-[#9F1D35]'}`} />
    {children}
  </p>
);

const ProcessCard = ({ step, index }) => {
  const Icon = step.icon;
  const isDark = step.tone === 'burgundy';
  return (
    <article className={`landing-reveal snap-start relative min-h-[310px] min-w-[82vw] overflow-hidden rounded-[18px] border p-6 sm:min-w-[390px] ${toneClasses[step.tone]}`}>
      <AdirePattern className={`absolute inset-0 h-full w-full ${isDark ? 'text-[#FAFAF7]' : 'text-[#9F1D35]'}`} opacity={isDark ? 0.09 : 0.055} />
      <div className="relative flex items-start justify-between">
        <div className={`flex h-12 w-12 items-center justify-center rounded-full ${isDark ? 'border border-[#FAFAF7]/25 bg-[#FAFAF7]/10' : 'bg-[#F5E8EA]'}`}>
          <Icon className={`h-6 w-6 ${isDark ? 'text-[#FAFAF7]' : 'text-[#9F1D35]'}`} strokeWidth={1.5} />
        </div>
        <span className={`font-serif text-[30px] leading-none ${isDark ? 'text-[#FAFAF7]/35' : 'text-[#D8D5D0]'}`}>
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>
      <div className="relative mt-7">
        <h3 className={`font-serif text-2xl font-medium leading-tight tracking-normal ${isDark ? 'text-[#FAFAF7]' : 'text-[#1A1A1A]'}`}>{step.title}</h3>
        <p className={`mt-4 text-sm leading-7 ${isDark ? 'text-[#F5E8EA]' : 'text-[#5A5A5A]'}`}>{step.description}</p>
      </div>
    </article>
  );
};

const PhoneFrame = ({ screen }) => (
  <article className="landing-reveal snap-start min-w-[82vw] sm:min-w-[340px]">
    <div className="mx-auto w-full max-w-[330px] rounded-[30px] border border-[#D8D5D0] bg-[#FAFAF7] p-2">
      <div className="relative overflow-hidden rounded-[24px] bg-[#F7F4EE] p-4">
        <AdirePattern className="absolute inset-0 h-full w-full text-[#9F1D35]" opacity={0.035} />
        <div className="relative mb-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#9F1D35]">{screen.eyebrow}</p>
            <h3 className="mt-1 font-serif text-xl font-semibold text-[#1A1A1A]">{screen.title}</h3>
          </div>
          <span className="h-8 w-8 rounded-full bg-[#9F1D35]" />
        </div>
        <PhoneScreen type={screen.type} />
      </div>
    </div>
    <p className="mx-auto mt-5 max-w-[330px] text-sm leading-7 text-[#5A5A5A]">{screen.summary}</p>
  </article>
);

const PhoneScreen = ({ type }) => {
  if (type === 'dashboard') {
    return (
      <div className="relative space-y-3">
        <div className="rounded-2xl bg-[#9F1D35] p-4 text-[#FAFAF7]">
          <p className="text-xs text-[#F5E8EA]">Next visit</p>
          <p className="mt-1 font-serif text-2xl">Today, 10:30 WAT</p>
          <p className="mt-2 text-xs text-[#F5E8EA]">Mama Adaeze | Lagos</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <MiniMetric label="Care officer" value="Eniola" />
          <MiniMetric label="Alerts" value="2 active" />
        </div>
        <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-3">
          <p className="text-xs font-semibold text-[#1A1A1A]">Pending approval</p>
          <p className="mt-1 text-xs text-[#5A5A5A]">Grocery restock | N/A</p>
        </div>
      </div>
    );
  }
  if (type === 'report') {
    return (
      <div className="space-y-3">
        <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4">
          <div className="flex items-center justify-between">
            <p className="font-serif text-4xl text-[#2F8C55]">81</p>
            <span className="rounded-full bg-green-50 px-2 py-1 text-[10px] font-semibold text-green-700">Approved</span>
          </div>
          <p className="mt-1 text-xs text-[#5A5A5A]">Overall wellness</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <MiniMetric label="Temp" value="36.5 C" />
          <MiniMetric label="BP" value="115/73" />
        </div>
        <div className="rounded-2xl bg-white p-3 text-xs leading-5 text-[#5A5A5A]">
          Vitals normal. Food prepared. Pantry restock recommended this week.
        </div>
      </div>
    );
  }
  if (type === 'evidence') {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {EVIDENCE_PHOTOS.map((photo) => (
            <figure key={photo.label} className="relative aspect-square overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
              <img
                src={photo.src}
                alt={photo.alt}
                className="photo-warm h-full w-full object-cover"
                loading="lazy"
              />
              <figcaption className="absolute inset-x-2 bottom-2 rounded-xl bg-[#1A1A1A]/70 px-2 py-1 backdrop-blur-sm">
                <span className="block text-[10px] font-semibold text-[#FAFAF7]">{photo.label}</span>
                <span className="block text-[9px] text-[#F5E8EA]">{photo.meta}</span>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-3">
          <p className="text-xs font-semibold text-[#1A1A1A]">Video evidence</p>
          <div className="mt-2 h-2 rounded-full bg-[#F5E8EA]">
            <div className="h-2 w-2/3 rounded-full bg-[#9F1D35]" />
          </div>
        </div>
      </div>
    );
  }
  if (type === 'chat') {
    return (
      <div className="space-y-3">
        <ChatBubble text="Good morning. I just arrived for the visit." />
        <ChatBubble mine text="Thank you. Please check her medication pack." />
        <ChatBubble text="Checked. Two days left, so I will raise an alert." />
        <div className="rounded-full border border-[#E8E6E3] bg-white px-4 py-3 text-xs text-[#5A5A5A]">Type a message...</div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {['Blood pressure above normal', 'Groceries running low', 'Medication refill due'].map((item, index) => (
        <div key={item} className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-3">
          <div className="flex items-start gap-2">
            <span className={`mt-1 h-2 w-2 rounded-full ${index === 0 ? 'bg-red-500' : 'bg-[#C9A961]'}`} />
            <div>
              <p className="text-xs font-semibold text-[#1A1A1A]">{item}</p>
              <p className="mt-1 text-[10px] text-[#5A5A5A]">{index === 0 ? 'New' : 'Needs approval'}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const MiniMetric = ({ label, value }) => (
  <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-3">
    <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#5A5A5A]">{label}</p>
    <p className="mt-2 text-sm font-semibold text-[#1A1A1A]">{value}</p>
  </div>
);

const ChatBubble = ({ text, mine = false }) => (
  <div className={`max-w-[86%] rounded-2xl px-3 py-2 text-xs leading-5 ${mine ? 'ml-auto bg-[#9F1D35] text-[#FAFAF7]' : 'bg-white text-[#1A1A1A]'}`}>
    {text}
  </div>
);

const FooterGroup = ({ heading, links }) => (
  <>
    <details className="group rounded-2xl border border-[#FAFAF7]/10 bg-[#FAFAF7]/5 p-4 sm:hidden">
      <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-[#FAFAF7]">
        {heading}
        <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
      </summary>
      <FooterLinks links={links} />
    </details>
    <div className="hidden rounded-2xl border border-[#FAFAF7]/10 bg-[#FAFAF7]/5 p-4 sm:block">
      <h3 className="text-sm font-medium text-[#FAFAF7]">{heading}</h3>
      <FooterLinks links={links} />
    </div>
  </>
);

const FooterLinks = ({ links }) => (
  <ul className="mt-4 space-y-3">
    {links.map(([label, href]) => (
      <li key={label}>
        {href.startsWith('/') ? (
          <Link to={href} className="text-sm text-[#FAFAF7]/80 transition-colors hover:text-[#FAFAF7]">{label}</Link>
        ) : (
          <a href={href} className="text-sm text-[#FAFAF7]/80 transition-colors hover:text-[#FAFAF7]">{label}</a>
        )}
      </li>
    ))}
  </ul>
);

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#F7F4EE] text-[#1A1A1A]" data-testid="landing-page">
      <nav className="fixed inset-x-0 top-0 z-50 overflow-hidden border-b border-[#E8E6E3]/70 bg-[#F7F4EE]/95 backdrop-blur">
        <AdirePattern className="pointer-events-none absolute inset-0 h-full w-full blur-[0.7px] text-[#9F1D35]" opacity={0.12} />
        <div className="relative mx-auto flex h-[72px] max-w-[1200px] items-center justify-between px-4 sm:px-5 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center">
            <LogoLockup size="md" />
          </Link>

          <div className="hidden items-center gap-8 md:flex">
            <a href="#how-it-works" className="text-sm font-medium text-[#1A1A1A] hover:text-[#9F1D35]">How it works</a>
            <a href="#family-dashboard" className="text-sm font-medium text-[#1A1A1A] hover:text-[#9F1D35]">Dashboard</a>
            <a href="#stories" className="text-sm font-medium text-[#1A1A1A] hover:text-[#9F1D35]">Stories</a>
            <a href="#coverage" className="text-sm font-medium text-[#1A1A1A] hover:text-[#9F1D35]">Coverage</a>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-4">
            <Link to="/login" className="hidden text-sm font-medium text-[#1A1A1A] hover:text-[#9F1D35] sm:inline" data-testid="nav-sign-in">
              Login
            </Link>
            <Link
              to="/register"
              className="rounded-full bg-[#9F1D35] px-3 py-2 text-xs font-medium text-[#FAFAF7] hover:bg-[#7A1628] sm:px-5 sm:py-2.5 sm:text-sm"
              data-testid="get-started-btn"
            >
              Create Account
            </Link>
          </div>
        </div>
      </nav>

      <section className="pt-[72px]">
        <div className="grid min-h-[calc(100vh-72px)] grid-cols-1 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="relative flex min-h-[calc(100vh-72px)] flex-col justify-center overflow-hidden px-6 py-14 sm:px-10 lg:px-16 xl:px-24">
            <div className="absolute inset-0 lg:hidden">
              <img
                src="/assets/magnolia-care-hero.png"
                alt=""
                className="photo-warm h-full w-full object-cover object-center opacity-35"
              />
              <div className="absolute inset-0 bg-[#F7F4EE]/78" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#F7F4EE]/55 via-[#F7F4EE]/80 to-[#F7F4EE]" />
            </div>
            <div className="pointer-events-none absolute -bottom-6 left-5 hidden font-serif text-[82px] font-medium leading-none tracking-normal text-[#E8E6E3] lg:block">
              magnolia<span className="text-[#D8D5D0]">.</span>
            </div>

            <div className="relative z-10 max-w-[590px]">
              <Eyebrow>Care, across continents</Eyebrow>

              <h1 className="display-xl font-medium text-[#1A1A1A]">
                Distance is hard.
                <span className="block italic text-[#9F1D35]">But there is hope.</span>
              </h1>

              <p className="body-lg mt-8 max-w-[520px] text-[#5A5A5A]">
                Magnolia coordinates trusted in-home care visits for your parents back home, helps procure necessary medications on time through our partner pharmacy chains, supports administration when required, and delivers verified reports, photo/video evidence, alerts, and care updates to your phone.
              </p>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center rounded-full bg-[#9F1D35] px-7 py-3.5 text-[15px] font-medium text-[#FAFAF7] hover:bg-[#7A1628]"
                  data-testid="hero-cta"
                >
                  Create Account
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-full border border-[#9F1D35] px-7 py-3.5 text-[15px] font-medium text-[#9F1D35] hover:bg-[#F5E8EA]"
                  data-testid="hero-login-cta"
                >
                  Login
                </Link>
                <button className="inline-flex items-center justify-center gap-3 rounded-full px-2 py-2 text-[15px] font-medium text-[#1A1A1A] hover:text-[#9F1D35]">
                  <span className="flex h-[38px] w-[38px] items-center justify-center rounded-full border-[1.5px] border-[#9F1D35]">
                    <Play className="ml-0.5 h-4 w-4 fill-[#9F1D35] text-[#9F1D35]" strokeWidth={1.5} />
                  </span>
                  Watch our story
                </button>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-3 border-t border-[#E8E6E3] pt-6 sm:gap-0">
                {[
                  ['847+', 'Families connected'],
                  ['4.9', 'Family rating'],
                  ['NG | GH', 'Active countries']
                ].map(([value, label], index) => (
                  <div key={label} className={`${index > 0 ? 'sm:border-l sm:border-[#D8D5D0] sm:pl-6' : ''} ${index < 2 ? 'sm:pr-6' : ''}`}>
                    <p className="font-serif text-[24px] font-medium leading-none text-[#7A1628] sm:text-[30px]" data-stat>
                      {value}
                      {value === '4.9' && <span className="text-[#C9A961]">*</span>}
                    </p>
                    <p className="mt-2 text-[10px] font-medium uppercase tracking-[0.08em] text-[#5A5A5A] sm:text-[11px]">{label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3 text-xs font-medium text-[#5A5A5A]">
                <span className="uppercase tracking-[0.1em] text-[#9F1D35]">Trusted by families in:</span>
                <div className="flex flex-wrap items-center gap-2">
                  {TRUSTED_MARKETS.map((market) => (
                    <span key={market.code} className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E6E3] bg-[#FAFAF7]/80 px-2.5 py-1">
                      <img
                        src={market.src}
                        alt={`${market.name} flag`}
                        className="h-3.5 w-5 rounded-[2px] object-cover"
                        loading="lazy"
                      />
                      <span>{market.name}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="relative hidden min-h-[560px] overflow-hidden bg-[#9F1D35] lg:block">
            <AdirePattern className="absolute inset-0 h-full w-full text-[#FAFAF7]" opacity={0.09} />
            <img
              src="/assets/magnolia-care-hero.png"
              alt="Warm in-home care scene"
              className="photo-warm absolute -left-8 bottom-0 top-20 h-auto max-h-[calc(100%-5rem)] w-[82%] rounded-tr-[18px] border border-b-0 border-l-0 border-[#F5E8EA]/20 object-contain object-left-bottom opacity-95"
            />
            <div className="absolute bottom-8 right-8 max-w-[220px] rounded-2xl border border-[#FAFAF7]/15 bg-[#7A1628]/72 p-5 text-[#FAFAF7] backdrop-blur">
              <p className="font-serif text-3xl leading-none">Care you can see.</p>
              <p className="mt-3 text-sm leading-6 text-[#F5E8EA]">Reports, evidence, alerts, and receipts in one family dashboard.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="relative overflow-hidden px-5 py-20 lg:px-8 lg:py-24">
        <AdirePattern className="absolute inset-0 h-full w-full text-[#9F1D35]" opacity={0.035} />
        <div className="relative mx-auto max-w-[1200px]">
          <div className="mb-10 max-w-3xl">
            <Eyebrow>How Magnolia works</Eyebrow>
            <h2 className="font-serif text-[40px] font-medium leading-[1.05] tracking-normal text-[#1A1A1A] sm:text-[56px]">
              Here are the steps to <span className="italic text-[#9F1D35]">peace of mind.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-[15px] leading-7 text-[#5A5A5A]">
              From signup to verified visit reports, Magnolia makes care, medication procurement, and family approvals visible, structured, and easy to follow from anywhere.
            </p>
          </div>
          <div className="landing-scroll -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-5 pb-4">
            {PROCESS_STEPS.map((step, index) => (
              <ProcessCard key={step.title} step={step} index={index} />
            ))}
          </div>
          <div className="landing-reveal mt-8 flex items-center gap-8 overflow-x-auto whitespace-nowrap pb-3">
            <span className="shrink-0 text-base font-semibold text-[#1A1A1A]">Medication from:</span>
            {PHARMACY_NETWORK.map((pharmacy) => (
              <img
                key={pharmacy.name}
                src={pharmacy.src}
                alt={pharmacy.name}
                className="h-14 w-auto max-w-[280px] shrink-0 object-contain sm:h-16"
                loading="lazy"
              />
            ))}
            <span className="shrink-0 text-base font-medium text-[#5A5A5A]">amongst others</span>
          </div>
        </div>
      </section>

      <section id="family-dashboard" className="border-y border-[#E8E6E3] bg-[#FAFAF7] px-5 py-20 lg:px-8">
        <div className="mx-auto max-w-[1200px]">
          <div className="mb-10 grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
            <div>
              <Eyebrow>The family dashboard</Eyebrow>
              <h2 className="font-serif text-[38px] font-medium leading-tight tracking-normal text-[#1A1A1A] sm:text-[52px]">
                Everything you need to know.
              </h2>
            </div>
            <p className="text-[15px] leading-7 text-[#5A5A5A]">
              You can review visit reports, see photo and video evidence, chat with linked care officers, approve medication and care expenses, and track alerts without hunting through messages and calls.
            </p>
          </div>

          <div id="care-reports" className="landing-scroll -mx-5 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-5 pb-5">
            {MOCK_SCREENS.map((screen) => (
              <PhoneFrame key={screen.title} screen={screen} />
            ))}
          </div>
        </div>
      </section>

      <section id="stories" className="relative overflow-hidden px-5 py-20 lg:px-8">
        <AdirePattern className="absolute inset-0 h-full w-full text-[#9F1D35]" opacity={0.035} />
        <div className="relative mx-auto max-w-[1200px]">
          <div className="mb-10 max-w-2xl">
            <Eyebrow>Stories of connection</Eyebrow>
            <h2 className="font-serif text-[40px] font-medium leading-tight tracking-normal text-[#1A1A1A] sm:text-[52px]">
              Families stay close, even from far away<span className="text-[#9F1D35]">.</span>
            </h2>
          </div>

          <div className="landing-scroll -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-5 pb-4">
            {STORIES.map((story) => (
              <article key={story.name} className="landing-reveal relative min-w-[82vw] snap-start overflow-hidden rounded-[18px] border border-[#E8E6E3] bg-[#FAFAF7] p-6 sm:min-w-[360px]">
                <AdirePattern className="absolute inset-0 h-full w-full text-[#9F1D35]" opacity={0.045} />
                <div className="relative mb-5 flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="h-4 w-4 fill-[#C9A961] text-[#C9A961]" />
                  ))}
                </div>
                <p className="relative text-[17px] leading-8 text-[#1A1A1A]">"{story.quote}"</p>
                <div className="relative mt-8 flex items-center gap-3">
                  <img src={story.image} alt={story.name} className="h-14 w-14 rounded-full object-cover" />
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{story.name}</p>
                    <p className="flex items-center gap-1 text-sm text-[#5A5A5A]">
                      <MapPin className="h-3 w-3" />
                      {story.location}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="bg-[#FAFAF7] px-5 py-16 lg:px-8">
        <div className="mx-auto grid max-w-[1200px] gap-4 lg:grid-cols-3">
          {[
            [Globe, 'About Magnolia', 'Magnolia helps diaspora families coordinate dependable in-home care, medication support, and practical follow-through for parents and loved ones back home.'],
            [MapPin, 'Coverage', 'Starting with Nigeria and Ghana, Magnolia schedules care using local availability, WAT timing, and verified home locations.'],
            [BadgeCheck, 'Partners', 'Care officers, supervisors, pharmacy chains, vendors, and support teams work together so family care does not depend on one phone call.']
          ].map(([Icon, title, text], index) => (
            <article key={title} id={index === 1 ? 'coverage' : index === 2 ? 'partners' : undefined} className="rounded-[18px] border border-[#E8E6E3] bg-[#F7F4EE] p-7">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[#F5E8EA]">
                <Icon className="h-6 w-6 text-[#9F1D35]" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-2xl font-medium text-[#1A1A1A]">{title}<span className="text-[#9F1D35]">.</span></h3>
              <p className="mt-4 text-sm leading-7 text-[#5A5A5A]">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#9F1D35] px-5 py-16 text-[#FAFAF7] lg:px-8 lg:py-20">
        <AdirePattern className="absolute inset-0 h-full w-full text-[#FAFAF7]" opacity={0.08} />
        <div className="relative mx-auto grid max-w-[1200px] gap-10 lg:grid-cols-[0.95fr_1fr] lg:items-center">
          <div className="landing-reveal overflow-hidden rounded-[22px] border border-[#FAFAF7]/18 bg-[#7A1628]">
            <img
              src="/assets/magnolia-family-care-living-room.png"
              alt="An older parent and adult child smiling together at home"
              className="photo-warm h-full min-h-[360px] w-full object-cover object-center lg:min-h-[520px]"
              loading="lazy"
            />
          </div>

          <div className="max-w-[560px] lg:justify-self-end">
            <Eyebrow light>Technology for saving lives</Eyebrow>
            <h2 className="font-serif text-[42px] font-medium leading-tight tracking-normal sm:text-[56px]">
              Your parents deserve care that reaches them.
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-[#F5E8EA]">
              Join families using Magnolia to stay close to the people who raised them.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/register"
                className="rounded-full bg-[#FAFAF7] px-8 py-4 text-center text-[15px] font-medium text-[#9F1D35] hover:bg-[#F5E8EA]"
              >
                Create Account
              </Link>
              <Link to="/login" className="rounded-full border border-[#FAFAF7] px-8 py-4 text-center text-[15px] font-medium text-[#FAFAF7] hover:bg-[#FAFAF7]/10">
                Login
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="relative overflow-hidden bg-[#1A1A1A] px-5 py-12 text-[#FAFAF7] lg:px-8">
        <AdirePattern className="absolute inset-0 h-full w-full text-[#FAFAF7]" opacity={0.055} />
        <div className="relative mx-auto grid max-w-[1200px] gap-8 lg:grid-cols-[1fr_1.4fr]">
          <div className="max-w-sm">
            <LogoLockup variant="inverse" size="md" />
            <p className="mt-5 text-sm leading-7 text-[#FAFAF7]/80">
              Premium care coordination for diaspora families with parents back home in Africa.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-[1fr_1fr_1.15fr]">
            {Object.entries(FOOTER_LINKS).map(([heading, links]) => (
              <FooterGroup key={heading} heading={heading} links={links} />
            ))}
            <div className="rounded-2xl border border-[#FAFAF7]/10 bg-[#FAFAF7]/5 p-4">
              <h3 className="text-sm font-medium text-[#FAFAF7]">Contact</h3>
              <ul className="mt-4 space-y-3 text-sm text-[#FAFAF7]/80">
                <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-[#C9A961]" /> support@magnolia.care</li>
                <li className="flex items-center gap-3"><MapPin className="h-4 w-4 text-[#C9A961]" /> Lagos | London</li>
                <li className="flex items-center gap-3"><PhoneCall className="h-4 w-4 text-[#C9A961]" /> Emergency support</li>
                <li className="flex items-center gap-3"><MessageCircle className="h-4 w-4 text-[#C9A961]" /> In-app care messages</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="relative mx-auto mt-10 flex max-w-[1200px] flex-col gap-2 border-t border-[#FAFAF7]/10 pt-6 text-sm text-[#FAFAF7]/70 sm:flex-row sm:items-center sm:justify-between">
          <span>Magnolia Care - Because no parent should be forgotten.</span>
          <span>
            Powered by{' '}
            <a
              href="https://www.thcohq.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[#FAFAF7] underline-offset-4 hover:text-[#C9A961] hover:underline"
            >
              THCO
            </a>
            . 2026
          </span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
